import * as React from 'react';
import { isEqual, uniq } from 'lodash';
import { Editor, EditorState, ContentState, ContentBlock, CompositeDecorator } from 'draft-js';

//@ts-ignore
import ComposerFilePreview from 'chat/components/ComposerFilePreview';
// TODO: once ComposerFilePreview is updated and doesn't need a `File`,
// we can remove this
import File from 'entities/File';
import Link from 'components/Link';
import { Picker, Uploader } from 'components/File';
import Text from 'components/Text';
import TextWithGlyph from 'components/TextWithGlyph';
import TranslateButton from 'components/TranslateButton';

//@ts-ignore
import { URGENT_STRING } from 'chat/components/SplitComposer/components/ComposeMessage';

import 'draft-js/dist/Draft.css';
import './styles/ComposerTextboxWithTokens.scss';

type DoubleBracketBookends = {
  start: '{{';
  end: '}}';
};

export type Props = {
  placeholder?: string;
  value: string;
  fileUpload?: File;
  isUrgent?: boolean;
  // callback that triggers whenever the text in the
  // textbox changes
  onChange: (sanitizedText: string) => void;
  // callback that triggers whenever the array of distinct
  // tokens changes
  onTokenChange: (tokens: string[]) => void;
  // callback that triggers whenever the number of distinct
  // tokens in the textbox increases
  onTokenIncrease?: () => void;
  // callback that triggers whenever a file is attached
  onFileUpload?: (fileUuid?: string) => void;
} & DefaultProps;

type DefaultProps = {
  // the characters that identify the beginning and
  // end of a token
  tokenBookends: DoubleBracketBookends;
  allowTranslation: boolean;
};

type State = {
  editorState: EditorState;
  tokens: string[];
};

export default class ComposerTextboxWithTokens extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { start, end } = props.tokenBookends;
    this.completeTokenRegex = new RegExp(`(${start})(\\w)+(${end})|(${start})(${end})`, 'g');
    this.state = {
      editorState: this.composeEditorStateFromText(props.value),
      tokens: this.getTokens(props.value),
    };
  }

  static defaultProps: DefaultProps = {
    tokenBookends: {
      start: '{{',
      end: '}}',
    },
    allowTranslation: true,
  };

  completeTokenRegex: RegExp;

  composeEditorStateFromText = (text: string) => {
    const tokenStrategy = (
      contentBlock: ContentBlock,
      callback: (start: number, end: number) => void
    ) => {
      findWithRegex(this.completeTokenRegex, contentBlock, callback);
    };
    const compositeDecorator = new CompositeDecorator([
      {
        strategy: tokenStrategy,
        component: TokenSpan,
      },
    ]);
    const contentState = ContentState.createFromText(text);
    return EditorState.createWithContent(contentState, compositeDecorator);
  };

  getTokens = (htmlString: string) => {
    const tokens = htmlString.match(this.completeTokenRegex) || [];
    const startBookendLength = this.props.tokenBookends.start.length;
    const endBookendLength = this.props.tokenBookends.end.length;
    const tokensWithoutBookends = tokens.map(token =>
      token.slice(startBookendLength, -endBookendLength)
    );
    return uniq(tokensWithoutBookends);
  };

  handleChange = (editorState: EditorState) => {
    this.setState({ editorState }, () => {
      // call the onChange callback if the text has changed
      const sanitizedText = editorState.getCurrentContent().getPlainText();
      this.props.onChange(sanitizedText);

      // fetch tokens, save them and pass them to callback if they
      // have changed since the last time the user has typed
      const tokens = this.getTokens(sanitizedText);
      if (tokens.length > this.state.tokens.length && this.props.onTokenIncrease) {
        this.props.onTokenIncrease();
      }
      if (!isEqual(tokens, this.state.tokens)) {
        this.setState({ tokens });
        this.props.onTokenChange(tokens);
      }
    });
  };

  handleFileUpload = (data: { id?: string }) => {
    if (this.props.onFileUpload) {
      this.props.onFileUpload(data.id);
    }
  };

  renderFileUpload = () => {
    const { fileUpload } = this.props;
    return (
      <Picker>
        {({ pick, file, onClear }) => {
          const handleDelete = () => {
            this.handleFileUpload({ id: undefined });
            onClear();
          };

          // if a file attachment has been picked by the user, show that file
          if (file) {
            return (
              <Uploader file={file} private={false} onUploadSuccess={this.handleFileUpload}>
                {({ progress, fileType, thumbnail }) => (
                  <ComposerFilePreview
                    progress={progress}
                    thumbnail={thumbnail}
                    type={fileType}
                    onDelete={handleDelete}
                  />
                )}
              </Uploader>
            );
          }

          // otherwise, if an existing file attachment is loaded in the props, show the existing file
          if (fileUpload) {
            return <ComposerFilePreview progress={100} file={fileUpload} onDelete={handleDelete} />;
          }

          // if no file attachments exist, show the picker
          return (
            <div className="ComposerTextboxWithTokens-fileAttachment-dropzone">
              <TextWithGlyph
                glyph="drag-and-drop"
                glyphColor="eclipse"
                glyphSize="xlarge"
                gutter="small"
                color="eclipse"
                center
                inline>
                <Link onClick={pick}>Click here to attach a file</Link>
              </TextWithGlyph>
            </div>
          );
        }}
      </Picker>
    );
  };

  handleTranslate = (translatedText: string) => {
    const newEditorState = this.composeEditorStateFromText(translatedText);
    this.handleChange(newEditorState);
  };

  renderTranslate = (editorState: EditorState) => {
    return (
      <TranslateButton
        textToTranslate={editorState.getCurrentContent().getPlainText()}
        onTextChange={this.handleTranslate}
        label={
          <Text color="pitch" size="xxxsmall">
            Translate &#9662;
          </Text>
        }
      />
    );
  };

  render() {
    const { placeholder, allowTranslation } = this.props;
    const { editorState } = this.state;

    // this will eventually expand to include other header actions that
    // we should check for
    const shouldShowHeader = allowTranslation;

    return (
      <div className="ComposerTextboxWithTokens">
        {shouldShowHeader && (
          <div className="ComposerTextboxWithTokens-editorHeader">
            {allowTranslation && this.renderTranslate(editorState)}
          </div>
        )}
        <div className="ComposerTextboxWithTokens-editorBody">
          <Editor
            placeholder={placeholder || 'Type here'}
            editorState={editorState}
            onChange={this.handleChange}
            stripPastedStyles
          />
        </div>
        <div className="ComposerTextboxWithTokens-fileAttachment">{this.renderFileUpload()}</div>
      </div>
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.isUrgent && this.props.isUrgent !== prevProps.isUrgent) {
      const newEditorState = this.composeEditorStateFromText(URGENT_STRING + this.props.value);
      this.handleChange(newEditorState);
    }
  }
}

function findWithRegex(
  regex: RegExp,
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void
) {
  const text = contentBlock.getText();
  let matchArr, start;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
}

function TokenSpan(props: { children: React.ReactNode }) {
  return <code className="ComposerTextboxWithTokens-token">{props.children}</code>;
}
